import React from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { List } from 'immutable'
import { connect as connectRedux, useDispatch, useSelector } from 'react-redux'
import { get as getConfig } from '../../../config/index'
import { getBoundActions } from '../../../actions/index'
import { Tabs, Tab, TABS_JUSTIFY } from '../../Tabs/index'
import { connect as connectStaticText } from '../../StaticText/connect'
import {
  connect as connectPage,
  PAGE_SEO_TYPE_LOCATION,
} from '../../Page/connect'
import GaiaLogo, { TYPE_TEAL } from '../../GaiaLogo/index'
import {
  H1,
  H2,
  H6,
  HEADING_TYPES,
} from '../../Heading/index'
import Link, { TARGET_SELF } from '../../Link/index'
import IconV2, { ICON_TYPES } from '../../Icon.v2/index'
import Sherpa, { TYPE_SMALL_BLUE } from '../../Sherpa/index'
import AmbassadorsPayouts from '../AmbassadorsPayouts/index'
import { selectUserFullName } from '../../../services/user/selectors'
import { selectStaticTextByKey } from '../../../services/static-text/selectors'
import {
  AMBASSADORS_PAGE_STATIC_KEY,
  PARTNER_DETAIL_TAB_VALUE,
  PARTNER_PAYOUTS_TAB_VALUE,
  PARTNER_SIGNUPS_TAB_VALUE,
  PARTNER_CAMPAIGNS_TAB_VALUE,
} from '../../../services/ambassadors/index'
import { NAVIGATION_HISTORY_PUSH } from '../../../services/navigation/actions'
import { selectLocation } from '../../../services/resolver/selectors'
import { selectAmbassadorCampaignDetails } from '../../../services/ambassadors/selectors'
import {
  PartnerCampaignsTab,
  PartnerDetailsTab,
  PartnerPayoutsTab,
  PartnerSignupsTab,
  PartnerCampaignDetailsTab,
} from './PartnerDashboardTabs'

const getTabs = (hasUtmCampaign) => {
  const staticText = useSelector(selectStaticTextByKey(AMBASSADORS_PAGE_STATIC_KEY))

  return [
    {
      label: staticText.get('partnerDetailsTabTitle', ''),
      value: PARTNER_DETAIL_TAB_VALUE,
      TabComponent: PartnerDetailsTab,
    },
    {
      label: staticText.get('partnerPayoutsTabTitle', ''),
      value: PARTNER_PAYOUTS_TAB_VALUE,
      TabComponent: PartnerPayoutsTab,
    },
    {
      label: staticText.get('partnerSignupsTabTitle', ''),
      value: PARTNER_SIGNUPS_TAB_VALUE,
      TabComponent: PartnerSignupsTab,
    },
    {
      label: staticText.get('partnerCampaignsTabTitle', ''),
      value: PARTNER_CAMPAIGNS_TAB_VALUE,
      TabComponent: hasUtmCampaign ? PartnerCampaignDetailsTab : PartnerCampaignsTab,
    },
  ]
}

/**
 * Render the Header or the breadcrumbs
 * @param {Object} props The props
 * @returns {JSX.Element}
 */
const AmbassadorsPageHeader = (props) => {
  const { hasUtmCampaign } = props
  const userFullName = useSelector(selectUserFullName)
  const campaignData = useSelector(selectAmbassadorCampaignDetails)

  if (hasUtmCampaign) {
    return (
      <div className="ambassadors-page__breadcrumbs">
        <Link
          className="ambassadors-page__breadcrumbs-name"
          to="/ambassadors?tab=partner-details"
          target={TARGET_SELF}
        >
          {userFullName}
        </Link>
        <IconV2
          className="ambassadors-page__breadcrumbs-icon"
          type={ICON_TYPES.CHEVRON_RIGHT}
        />
        <div className="ambassadors-page__breadcrumbs-campaign">
          {campaignData.get('name')}
        </div>
      </div>
    )
  }

  return (
    <H6 className="partner-page__sub-title">{userFullName}</H6>
  )
}

/**
 * Ambassadors Page
 * @param {Object} props The props
 * @param {Map} props.auth The auth object data
 * @param {Map} props.staticText The staticText object data
 * @returns The complete AmbassadorsPage component
 */
const AmbassadorsPage = (props) => {
  const {
    getAmbassadorPayouts,
    staticText,
    processing,
    payouts,
    locale,
    history,
    resolver,
  } = props
  const location = useSelector(selectLocation)
  const dispatch = useDispatch()

  if (getConfig().features.partnerDashboard) {
    const activeTabValue = location?.query?.tab
    const hasUtmCampaign = !!(location?.query?.utmCampaign)
    const tabs = getTabs(hasUtmCampaign)
    const activeTabIndex = tabs.findIndex(({ value }) => value === activeTabValue)

    const handleTabChange = (index) => {
      const query = {
        tab: tabs[index]?.value,
      }

      dispatch({
        type: NAVIGATION_HISTORY_PUSH,
        payload: { url: location.pathname, query },
      })
    }

    return (
      <div className="partner-page">
        <H1 className="partner-page__title">{staticText.getIn(['data', 'partnerTitle'])}</H1>
        <AmbassadorsPageHeader hasUtmCampaign={hasUtmCampaign} />
        <Tabs
          justify={TABS_JUSTIFY.LEFT}
          activeTab={activeTabIndex !== -1 ? activeTabIndex : 0}
          onChange={handleTabChange}
        >
          {tabs.map(({ value, label, TabComponent }) => (
            <Tab key={value} label={label}>
              <TabComponent history={history} resolver={resolver} />
            </Tab>
          ))}
        </Tabs>
      </div>
    )
  }

  return (
    <div className="ambassadors-page">
      <GaiaLogo className={['ambassadors-page__logo']} type={TYPE_TEAL} isHref />
      <Link to="/" className="ambassadors-page__back">
        <IconV2 type={ICON_TYPES.CHEVRON_LEFT} />
        {staticText.getIn(['data', 'goBack'])}
      </Link>
      <H1 className="ambassadors-page__title">{staticText.getIn(['data', 'title'])}</H1>
      {processing ? <Sherpa className={['ambassadors-page__sherpa']} type={TYPE_SMALL_BLUE} /> : null}
      {payouts && !processing
        ? <div className="ambassadors-page__content">
          <H2
            as={HEADING_TYPES.H4}
            className="ambassadors-payouts__subtitle"
          >
            {staticText.getIn(['data', 'subtitle'])}
          </H2>
          <AmbassadorsPayouts
            getAmbassadorPayouts={getAmbassadorPayouts}
            payouts={payouts}
            locale={locale}
          />
        </div>
        : null}
    </div>
  )
}

AmbassadorsPage.propTypes = {
  locale: PropTypes.string.isRequired,
  processing: PropTypes.bool.isRequired,
  payouts: ImmutablePropTypes.list.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  getAmbassadorPayouts: PropTypes.func.isRequired,
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION }),
  connectStaticText({ storeKey: 'ambassadorsPage' }),
  connectRedux(
    (state) => ({
      payouts: state.ambassadors.getIn(['payouts', 'payouts'], List()),
      processing: state.ambassadors.get('processing', true),
      locale: state.page.get('locale'),
      resolver: state.resolver,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        getAmbassadorPayouts: actions.ambassadors.getAmbassadorPayouts,
      }
    },
  ),
)(AmbassadorsPage)
